import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { IconComponent, SkeletonComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { IconType } from '@kinomap-dev/ng-ui-lib/core';
import { GoalActivity } from 'app/features/goal/models/goal.models';
import {
  PaginationAppearance,
  PaginationComponent,
} from 'app/shared/ui/pagination/pagination.component';
import { PaginationMeta } from 'app/shared/models/pagination.models';

type GoalActivityRow = {
  activity: GoalActivity;
  id: string;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-goal-activities-section',
  imports: [DatePipe, IconComponent, PaginationComponent, SkeletonComponent],
  templateUrl: './goal-activities-section.component.html',
  styleUrl: './goal-activities-section.component.scss',
})
export class GoalActivitiesSectionComponent {
  readonly paginationAppearance: PaginationAppearance = {
    activeBackgroundColor: '#1e1e1e',
    activeTextColor: '#ffffff',
    borderColor: '#d6d6d6',
    buttonBackgroundColor: '#ffffff',
    buttonTextColor: '#1e1e1e',
  };

  readonly activities: InputSignal<GoalActivity[]> = input.required<GoalActivity[]>();
  readonly areActivitiesExpanded: InputSignal<boolean> = input.required<boolean>();
  readonly isLoading: InputSignal<boolean> = input.required<boolean>();
  readonly shouldShowToggle: InputSignal<boolean> = input.required<boolean>();
  readonly paginationMeta: InputSignal<PaginationMeta | null> =
    input.required<PaginationMeta | null>();
  readonly activityRows: Signal<GoalActivityRow[]> = computed<GoalActivityRow[]>(() => {
    const occurrenceBySignature: Map<string, number> = new Map<string, number>();

    return this.activities().map((activity: GoalActivity) => {
      const signature: string = this.getActivitySignature(activity);
      const occurrence: number = occurrenceBySignature.get(signature) ?? 0;

      occurrenceBySignature.set(signature, occurrence + 1);

      return {
        activity,
        id: `${signature}:${occurrence}`,
      };
    });
  });
  readonly skeletonRows: Signal<number[]> = computed<number[]>(() => {
    const rowCount: number = Math.max(
      this.activities().length,
      this.areActivitiesExpanded() ? 10 : 5
    );

    return Array.from({ length: rowCount }, (_: unknown, index: number) => index);
  });

  readonly toggleExpansion: OutputEmitterRef<void> = output<void>();
  readonly activitiesPageChange: OutputEmitterRef<number> = output<number>();

  getGenderIconName(activity: GoalActivity): IconType {
    return (activity.gender || 'male') as IconType;
  }

  private getActivitySignature(activity: GoalActivity): string {
    return JSON.stringify([
      activity.start_date,
      activity.duration,
      activity.distance,
      activity.percentage,
      activity.firstname,
      activity.lastname,
      activity.gender,
    ]);
  }
}
